import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import { Box, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { CleaningMission, CleaningMissionComment, Cloud } from '@silbo/core';
import { colors, palette } from '@silbo/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from '../../../toast';
import { ago } from '../utils/elapsed';

type MissionCommentsProps = {
  mission: CleaningMission;
  userId?: string;
  canComment: boolean;
  now: Date;
};

const Bubble = ({ comment, isMine, now }: { comment: CleaningMissionComment; isMine: boolean; now: Date }) => {
  const { t } = useTranslation('environmental-cleaning');
  const author = isMine ? t('mission.you') : comment.authorName;
  return (
    <Stack
      gap="3px"
      sx={{
        alignSelf: isMine ? 'flex-end' : 'flex-start',
        alignItems: isMine ? 'flex-end' : 'flex-start',
        maxWidth: '82%',
      }}
    >
      <Typography sx={{ fontSize: 11, color: 'text.secondary', paddingX: 1 }}>
        {author} · {ago(t, comment.at, now)}
      </Typography>
      <Box
        sx={{
          paddingY: 2,
          paddingX: '11px',
          fontSize: 13,
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          borderRadius: isMine ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
          bgcolor: isMine ? palette.primary.main : colors.grey[5],
          color: isMine ? 'common.white' : 'text.primary',
        }}
      >
        {comment.text}
      </Box>
    </Stack>
  );
};

const MissionComments = ({ mission, userId, canComment, now }: MissionCommentsProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const [comment, setComment] = useState('');
  const [isSending, setSending] = useState(false);
  const hasText = comment.trim().length > 0;
  const count = mission.comments.length;

  const sendComment = async () => {
    setSending(true);
    try {
      await Cloud.commentCleaningMission({ missionId: mission.id, text: comment.trim() });
      setComment('');
    } catch {
      toast.error(t('mission.commentError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack gap="10px">
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {count > 0 ? t('mission.commentsCount', { count }) : t('mission.noComments')}
      </Typography>
      {mission.comments.map((item) => (
        <Bubble key={item.id} comment={item} isMine={!!userId && item.authorId === userId} now={now} />
      ))}
      {canComment && (
        <Stack direction="row" alignItems="flex-end" gap={2}>
          <InputBase
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t('mission.commentPlaceholder')}
            multiline
            maxRows={4}
            sx={{
              flex: 1,
              minHeight: 44,
              paddingY: '10px',
              paddingX: 4,
              fontSize: 15,
              border: `1.5px solid ${colors.grey[20]}`,
              borderRadius: '22px',
            }}
          />
          {hasText && (
            <IconButton
              aria-label={t('mission.sendComment')}
              onClick={sendComment}
              disabled={isSending}
              sx={{
                flex: 'none',
                width: 36,
                height: 36,
                marginBottom: 1,
                bgcolor: colors.blue[20],
                color: 'primary.main',
              }}
            >
              <ArrowUpwardRounded sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default MissionComments;
